"use client";

import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import {
  getMediaIdsReferencedByOwnerRelation,
  getPyloMediaList,
  type MediaUsageRelation,
} from "@/services/pylo-media.service";
import { runEntityAggregateQuery } from "@/services/entity-aggregate.service";
import { useCustomerId } from "@/contexts/customer-id-context";
import {
  buildMediaQuery,
  buildMediaSort,
  MEDIA_KINDS,
  type MediaKind,
  type MediaLibraryFilter,
  type UsedByFilter,
} from "@/lib/media-library-filter";

const MEDIA_ENTITY = "PyloMedia";
const PAGE_SIZE = 48;

interface OwnerUsedByPrefetch {
  ids?: Array<string>;
  isLoading: boolean;
}

/**
 * Owner-source `usedBy` filters can't be applied directly to `PyloMedia` —
 * there's no inverse field to walk. Pre-resolve the referenced media IDs so
 * the list can filter `id IN (…)` on top of them.
 */
const useUsedByOwnerMediaIds = (
  usedBy: UsedByFilter | undefined,
  relations: Array<MediaUsageRelation>,
): OwnerUsedByPrefetch => {
  const relation = useMemo(
    () =>
      usedBy?.source === "owner"
        ? relations.find(
            (candidate) =>
              candidate.source === "owner" &&
              candidate.fieldName === usedBy.fieldName &&
              candidate.entityName === usedBy.entityName,
          )
        : undefined,
    [usedBy, relations],
  );

  const enabled = !!relation;
  const query = useQuery({
    queryKey: [
      "media-usedby-owner",
      relation?.entityName,
      relation?.fieldName,
      usedBy?.instanceId ?? null,
    ],
    queryFn: () => getMediaIdsReferencedByOwnerRelation(relation!, usedBy?.instanceId),
    enabled,
  });

  if (!enabled) return { ids: undefined, isLoading: false };
  return { ids: query.data, isLoading: query.isLoading };
};

export const useMediaList = (filter: MediaLibraryFilter, relations: Array<MediaUsageRelation>) => {
  const customerId = useCustomerId();
  const usedByOwner = useUsedByOwnerMediaIds(filter.usedBy, relations);

  const query = useMemo(
    () => buildMediaQuery(filter, relations, usedByOwner.ids),
    [filter, relations, usedByOwner.ids],
  );
  const sortby = useMemo(() => buildMediaSort(filter), [filter]);

  // Hold off firing the list until we know which ids the owner-source usedBy
  // resolves to; otherwise the first page renders unfiltered results.
  const waitingOnOwnerIds = filter.usedBy?.source === "owner" && usedByOwner.ids === undefined;

  return useInfiniteQuery({
    queryKey: ["media-library", customerId, query, sortby],
    queryFn: ({ pageParam = 1 }) =>
      getPyloMediaList({
        filter: { query, sortby },
        pagination: { page: pageParam, per_page: PAGE_SIZE },
      }),
    getNextPageParam: (lastPage) =>
      lastPage?.pagination?.has_more_pages ? lastPage.pagination.current_page + 1 : undefined,
    initialPageParam: 1,
    enabled: !waitingOnOwnerIds,
  });
};

const kindOfMimeType = (mimeType: string | null): MediaKind => {
  if (!mimeType) return "other";
  if (mimeType.startsWith("image/")) return "image";
  if (mimeType.startsWith("video/")) return "video";
  if (mimeType.startsWith("audio/")) return "audio";
  if (mimeType.startsWith("application/") || mimeType.startsWith("text/")) return "document";
  return "other";
};

/**
 * Counts per file kind, computed server-side. The kind filter itself is dropped
 * from the aggregate so the facet keeps showing what selecting a *different*
 * kind would yield — standard facet behaviour.
 */
export const useMediaKindFacets = (
  filter: MediaLibraryFilter,
  relations: Array<MediaUsageRelation>,
) => {
  const customerId = useCustomerId();
  const usedByOwner = useUsedByOwnerMediaIds(filter.usedBy, relations);
  const query = useMemo(
    () => buildMediaQuery({ ...filter, kind: undefined }, relations, usedByOwner.ids),
    [filter, relations, usedByOwner.ids],
  );
  const waitingOnOwnerIds = filter.usedBy?.source === "owner" && usedByOwner.ids === undefined;

  const aggregate = useQuery({
    queryKey: ["media-library-kind-facets", customerId, query],
    queryFn: () =>
      runEntityAggregateQuery({
        entityName: MEDIA_ENTITY,
        metrics: [{ function: "count", field: "*", alias: "count" }],
        dimensions: [{ kind: "field", field: "file_mime_type" }],
        filter: query,
      }),
    enabled: !waitingOnOwnerIds,
  });

  const counts = useMemo(() => {
    const empty = Object.fromEntries(MEDIA_KINDS.map((kind) => [kind, 0])) as Record<
      MediaKind,
      number
    >;
    for (const row of aggregate.data?.rows ?? []) {
      const kind = kindOfMimeType((row.file_mime_type as string | null) ?? null);
      empty[kind] += Number(row.count ?? 0);
    }
    return empty;
  }, [aggregate.data]);

  return { counts, isLoading: aggregate.isLoading };
};

/** Total file count and combined size for the current filter. */
export const useMediaSummary = (
  filter: MediaLibraryFilter,
  relations: Array<MediaUsageRelation>,
) => {
  const customerId = useCustomerId();
  const usedByOwner = useUsedByOwnerMediaIds(filter.usedBy, relations);
  const query = useMemo(
    () => buildMediaQuery(filter, relations, usedByOwner.ids),
    [filter, relations, usedByOwner.ids],
  );
  const waitingOnOwnerIds = filter.usedBy?.source === "owner" && usedByOwner.ids === undefined;

  const aggregate = useQuery({
    queryKey: ["media-library-summary", customerId, query],
    queryFn: () =>
      runEntityAggregateQuery({
        entityName: MEDIA_ENTITY,
        metrics: [
          { function: "count", field: "*", alias: "count" },
          { function: "sum", field: "file_size_bytes", alias: "bytes" },
        ],
        filter: query,
      }),
    enabled: !waitingOnOwnerIds,
  });

  return {
    count: Number(aggregate.data?.total?.count ?? 0),
    bytes: Number(aggregate.data?.total?.bytes ?? 0),
    isLoading: aggregate.isLoading,
  };
};
